import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, ACCESS_LABELS } from "@/lib/utils2";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, KeyRound, Pencil, Trash2, CheckCircle2, Clock } from "lucide-react";

const ACCESS_FOR_ROLE = (role) =>
  ["operations", "management", "team_lead"].includes(role)
    ? ["admin", "podcast_creator", "consumer"]
    : ["consumer"];

function UserForm({ user, existing, isTeamLead, myStore, onDone }) {
  const isEdit = !!existing;
  const [form, setForm] = useState({
    phone: existing?.phone || "",
    first_name: existing?.first_name || "",
    last_name: existing?.last_name || "",
    store_number: existing?.store_number || (isTeamLead ? myStore : ""),
    role: existing?.role || "cafe_employee",
    access: existing?.access || ["consumer"],
  });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const allowed = ACCESS_FOR_ROLE(form.role);

  const setRole = (role) => {
    const a = form.access.filter((x) => ACCESS_FOR_ROLE(role).includes(x));
    if (!a.includes("consumer")) a.push("consumer");
    setForm({ ...form, role, access: a });
  };

  const toggleAccess = (a) => {
    if (a === "consumer") return;
    setForm((f) => ({ ...f, access: f.access.includes(a) ? f.access.filter((x) => x !== a) : [...f.access, a] }));
  };

  const save = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        await api.put(`/users/${existing.id}`, {
          first_name: form.first_name, last_name: form.last_name, store_number: form.store_number,
          role: form.role, access: form.access,
        });
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("Unclaimed account created");
      }
      onDone();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>First name</Label><Input data-testid="user-firstname" value={form.first_name} onChange={set("first_name")} className="mt-1.5 border-brand-line" /></div>
        <div><Label>Last name</Label><Input data-testid="user-lastname" value={form.last_name} onChange={set("last_name")} className="mt-1.5 border-brand-line" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Phone (username)</Label><Input data-testid="user-phone" value={form.phone} onChange={set("phone")} disabled={isEdit} className="mt-1.5 border-brand-line disabled:opacity-60" /></div>
        <div><Label>Store number</Label><Input data-testid="user-store" value={form.store_number} onChange={set("store_number")} disabled={isTeamLead} placeholder="numeric or Unset" className="mt-1.5 border-brand-line disabled:opacity-60" /></div>
      </div>
      <div>
        <Label>Role</Label>
        <Select value={form.role} onValueChange={setRole} disabled={isTeamLead && isEdit}>
          <SelectTrigger className="mt-1.5 border-brand-line" data-testid="user-role"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Access types</Label>
        <div className="mt-2 space-y-2">
          {["admin", "podcast_creator", "consumer"].map((a) => {
            const isAllowed = allowed.includes(a);
            return (
              <label key={a} className={`flex items-center gap-2.5 text-sm ${isAllowed ? "" : "opacity-40"}`}>
                <Checkbox checked={form.access.includes(a)} disabled={!isAllowed || a === "consumer"} onCheckedChange={() => toggleAccess(a)} data-testid={`access-${a}`} />
                {ACCESS_LABELS[a]}
                {a === "consumer" && <span className="text-xs text-brand-muted">(always on)</span>}
                {a === "admin" && form.role === "team_lead" && <span className="text-xs text-brand-muted">(limited)</span>}
              </label>
            );
          })}
        </div>
      </div>
      {!isEdit && <p className="text-xs text-brand-muted">Account starts unclaimed. The user claims it on the home page, logs in with the default password, and sets their own.</p>}
      <DialogFooter><Button onClick={save} disabled={busy} className="rounded-full brand-btn" data-testid="save-user-button">{isEdit ? "Save changes" : "Create account"}</Button></DialogFooter>
    </div>
  );
}

export default function Users() {
  const { user } = useAuth();
  const isTeamLead = user.role === "team_lead";
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => { const { data } = await api.get("/users"); setUsers(data.users); };
  useEffect(() => { load(); }, []);

  const resetPw = async (u) => {
    try { const { data } = await api.post(`/users/${u.id}/reset-password`); toast.success(data.message); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (u) => {
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl">
      <PageHeader
        title="User Management"
        subtitle={isTeamLead ? "As a Team Lead you can add users to your store and reset their passwords." : "Create unclaimed accounts, manage roles and access, and reset passwords."}
      >
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild><Button className="rounded-full brand-btn" data-testid="new-user-button"><Plus className="w-4 h-4 mr-2" /> New user</Button></DialogTrigger>
          <DialogContent className="max-w-lg border-brand-line">
            <DialogHeader><DialogTitle className="font-display text-2xl">Create unclaimed account</DialogTitle></DialogHeader>
            <UserForm user={user} isTeamLead={isTeamLead} myStore={user.store_number} onDone={() => { setCreateOpen(false); load(); }} />
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="rounded-2xl border border-brand-line bg-white overflow-hidden" data-testid="users-table">
        <div className="hidden md:grid grid-cols-[1.5fr_1fr_1fr_1.2fr_auto] gap-4 px-6 py-3 bg-brand-sidebar/60 text-xs uppercase tracking-widest text-brand-muted">
          <span>Name</span><span>Phone</span><span>Store</span><span>Role / Access</span><span>Actions</span>
        </div>
        {users.length === 0 && <p className="text-brand-muted text-sm p-6 text-center">No users yet.</p>}
        {users.map((u) => (
          <div key={u.id} className="grid md:grid-cols-[1.5fr_1fr_1fr_1.2fr_auto] gap-2 md:gap-4 px-6 py-4 border-t border-brand-line items-center" data-testid={`user-row-${u.id}`}>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-brand-ink">{u.first_name} {u.last_name}</span>
                {u.claimed ? <CheckCircle2 className="w-4 h-4 text-brand" title="Claimed" /> : <Clock className="w-4 h-4 text-brand-dough" title="Unclaimed" />}
              </div>
              <span className="text-xs text-brand-muted md:hidden">{u.phone} · Store {u.store_number}</span>
            </div>
            <span className="hidden md:block text-brand-muted">{u.phone}</span>
            <span className="hidden md:block text-brand-muted">{u.store_number}</span>
            <div className="flex flex-wrap gap-1">
              <Badge className="rounded-full border-0 bg-brand/10 text-brand">{ROLE_LABELS[u.role]}</Badge>
              {(u.access || []).filter((a) => a !== "consumer").map((a) => (
                <Badge key={a} className="rounded-full border-0 bg-brand-sidebar text-brand-muted">{ACCESS_LABELS[a]}</Badge>
              ))}
            </div>
            <div className="flex items-center gap-1 justify-end">
              <Button size="icon" variant="ghost" onClick={() => resetPw(u)} title="Reset password" data-testid={`reset-pw-${u.id}`}><KeyRound className="w-4 h-4" /></Button>
              {!isTeamLead && (
                <>
                  <Dialog open={editing?.id === u.id} onOpenChange={(o) => setEditing(o ? u : null)}>
                    <DialogTrigger asChild><Button size="icon" variant="ghost" title="Edit" data-testid={`edit-user-${u.id}`}><Pencil className="w-4 h-4" /></Button></DialogTrigger>
                    <DialogContent className="max-w-lg border-brand-line">
                      <DialogHeader><DialogTitle className="font-display text-2xl">Edit user</DialogTitle></DialogHeader>
                      <UserForm user={user} existing={u} isTeamLead={false} myStore={user.store_number} onDone={() => { setEditing(null); load(); }} />
                    </DialogContent>
                  </Dialog>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button size="icon" variant="ghost" className="text-brand-terracotta" title="Delete" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                    <AlertDialogContent className="border-brand-line">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-display text-2xl">Delete {u.first_name} {u.last_name}?</AlertDialogTitle>
                        <AlertDialogDescription>This permanently removes the account. This cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-md">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(u)} className="rounded-full bg-brand-terracotta hover:bg-brand-terracotta">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
